import { extensions } from "@repo/fika-blog/src/extensions"
import type {
  Principle,
  ProofreadDecoration,
} from "@repo/fika-blog/src/extensions/proofread"
import { renderToText } from "@repo/fika-blog/src/lib/render-to-text"
import type { Editor, JSONContent } from "@repo/fika-blog/src/tiptap/core"
import type { Locale } from "@repo/fika-shared/src/locale"
import { diffWordsWithSpace } from "diff"
import { chunk } from "remeda"
import { graphql, type VariablesOf } from "@/graphql"
import { ErrorFragment } from "@/lib/graphql-mutation-helpers"

const BATCH_SIZE = 3

interface TextChunk {
  id: string
  text: string
}

interface TextChange {
  type: "insert" | "delete" | "replace"
  originalStart: number
  originalEnd: number
  modifiedStart: number
  modifiedEnd: number
  originalText: string
  modifiedText: string
}

export function chunkByParagraphs(content: JSONContent): TextChunk[] {
  const chunks: TextChunk[] = []

  function traverse(doc: JSONContent): void {
    if (doc.type === "paragraph") {
      const text = renderToText({ doc, extensions: extensions() }).trim()
      if (!text) return

      const id = doc.attrs?.uid
      if (id) chunks.push({ id, text })
      return
    }

    if (doc.content && Array.isArray(doc.content)) {
      for (const child of doc.content) {
        traverse(child)
      }
    }
  }

  traverse(content)

  return chunks
}

interface RawChange {
  type: "insert" | "delete" | "unchanged"
  originalStart: number
  originalEnd: number
  modifiedStart: number
  modifiedEnd: number
  originalText: string
  modifiedText: string
}

function collectRawChanges(original: string, modified: string): RawChange[] {
  const diff = diffWordsWithSpace(original, modified)
  const rawChanges: RawChange[] = []

  let origPos = 0
  let modPos = 0

  for (const part of diff) {
    if (part.added) {
      rawChanges.push({
        type: "insert",
        originalStart: origPos,
        originalEnd: origPos,
        modifiedStart: modPos,
        modifiedEnd: modPos + part.value.length,
        originalText: "",
        modifiedText: part.value,
      })
      modPos += part.value.length
    } else if (part.removed) {
      rawChanges.push({
        type: "delete",
        originalStart: origPos,
        originalEnd: origPos + part.value.length,
        modifiedStart: modPos,
        modifiedEnd: modPos,
        originalText: part.value,
        modifiedText: "",
      })
      origPos += part.value.length
    } else {
      rawChanges.push({
        type: "unchanged",
        originalStart: origPos,
        originalEnd: origPos + part.value.length,
        modifiedStart: modPos,
        modifiedEnd: modPos + part.value.length,
        originalText: part.value,
        modifiedText: part.value,
      })
      origPos += part.value.length
      modPos += part.value.length
    }
  }

  return rawChanges
}

const MAX_BRIDGE_WORDS = 4
const SENTENCE_BOUNDARY_PATTERN = /[.!?]\s/

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length
}

function hasSentenceBoundary(text: string): boolean {
  return SENTENCE_BOUNDARY_PATTERN.test(text)
}

function shouldMergeAcrossGap(unchangedText: string): boolean {
  if (hasSentenceBoundary(unchangedText)) return false
  if (/^\s+$/.test(unchangedText)) return true
  return countWords(unchangedText) <= MAX_BRIDGE_WORDS
}

function mergeAdjacentChanges(rawChanges: RawChange[]): TextChange[] {
  const merged: TextChange[] = []

  let i = 0
  while (i < rawChanges.length) {
    const current = rawChanges[i]!

    if (current.type === "unchanged") {
      i++
      continue
    }

    const originalStart = current.originalStart
    let originalEnd = current.originalEnd
    const modifiedStart = current.modifiedStart
    let modifiedEnd = current.modifiedEnd
    let originalText = current.originalText
    let modifiedText = current.modifiedText

    let j = i + 1
    while (j < rawChanges.length) {
      const next = rawChanges[j]!

      if (next.type === "unchanged") {
        const nextChange = rawChanges[j + 1]
        const hasMoreChanges = nextChange && nextChange.type !== "unchanged"

        if (hasMoreChanges && shouldMergeAcrossGap(next.originalText)) {
          originalEnd = next.originalEnd
          modifiedEnd = next.modifiedEnd
          originalText += next.originalText
          modifiedText += next.modifiedText
          j++
          continue
        }
        break
      }

      originalEnd = next.originalEnd
      modifiedEnd = next.modifiedEnd
      originalText += next.originalText
      modifiedText += next.modifiedText
      j++
    }

    let type: "insert" | "delete" | "replace"
    let finalOriginalText = originalText
    let finalModifiedText = modifiedText

    // If the replacement is just whitespace, treat it as a deletion of non-whitespace
    // e.g., ", a sentence " -> " " becomes ", a sentence" -> ""
    if (/^\s*$/.test(modifiedText) && !/^\s*$/.test(originalText)) {
      const trimmedOriginal = originalText.trim()
      if (trimmedOriginal) {
        finalOriginalText = trimmedOriginal
        finalModifiedText = ""
      }
    }

    if (finalOriginalText === "") {
      type = "insert"
    } else if (finalModifiedText === "") {
      type = "delete"
    } else {
      type = "replace"
    }

    merged.push({
      type,
      originalStart,
      originalEnd,
      modifiedStart,
      modifiedEnd,
      originalText: finalOriginalText,
      modifiedText: finalModifiedText,
    })

    i = j
  }

  return merged
}

interface AdjacentWord {
  word: string
  newBoundary: number
}

function findPrevWord(text: string, pos: number): AdjacentWord | null {
  const match = text.slice(0, pos).match(/(\S+\s*)$/)
  if (!match) return null
  return { word: match[1]!, newBoundary: pos - match[1]!.length }
}

function findNextWord(text: string, pos: number): AdjacentWord | null {
  const match = text.slice(pos).match(/^(\s*\S+)/)
  if (!match) return null
  return { word: match[1]!, newBoundary: pos + match[1]!.length }
}

// So, we want to avoid inserts since they are hard to represent in the UI.
// We look for a previous or next word and expand the insertion as a replacement.
function expandInsertionsToReplacements(
  changes: TextChange[],
  original: string,
  modified: string,
): TextChange[] {
  return changes.map(change => {
    if (change.type !== "insert") return change

    const origPrev = findPrevWord(original, change.originalStart)
    const modPrev = findPrevWord(modified, change.modifiedStart)

    if (origPrev && modPrev) {
      return {
        type: "replace" as const,
        originalStart: origPrev.newBoundary,
        originalEnd: change.originalEnd,
        modifiedStart: modPrev.newBoundary,
        modifiedEnd: change.modifiedEnd,
        originalText: origPrev.word,
        modifiedText: modPrev.word + change.modifiedText,
      }
    }

    const origNext = findNextWord(original, change.originalStart)
    const modNext = findNextWord(modified, change.modifiedEnd)

    if (origNext && modNext) {
      return {
        type: "replace" as const,
        originalStart: change.originalStart,
        originalEnd: origNext.newBoundary,
        modifiedStart: change.modifiedStart,
        modifiedEnd: modNext.newBoundary,
        originalText: origNext.word,
        modifiedText: change.modifiedText + modNext.word,
      }
    }

    return change
  })
}

function normalizeQuotes(text: string): string {
  return text.replaceAll(/[""”“]/g, '"').replaceAll(/[''’]/g, "'")
}

export function findTextChanges(
  original: string,
  modified: string,
): TextChange[] {
  const normalizedOriginal = normalizeQuotes(original)
  const normalizedModified = normalizeQuotes(modified)
  const rawChanges = collectRawChanges(normalizedOriginal, normalizedModified)
  const merged = mergeAdjacentChanges(rawChanges)
  return expandInsertionsToReplacements(merged, original, modified)
}

function processChunkResult({
  editor,
  chunk,
  modifiedText,
  principle,
}: {
  editor: Editor
  chunk: TextChunk
  modifiedText: string | null
  principle: Principle
}) {
  const decorations: ProofreadDecoration[] = []

  if (!modifiedText || modifiedText === chunk.text) return decorations

  const paragraphPos = editor.$node("paragraph", { uid: chunk.id })
  if (paragraphPos === null) return decorations

  const changes = findTextChanges(chunk.text, modifiedText)

  const contentStart = paragraphPos.from

  for (const change of changes) {
    const from = contentStart + change.originalStart
    const to = contentStart + change.originalEnd

    decorations.push({
      id: `${chunk.id}-${principle}-${decorations.length}`,
      from,
      to,
      originalText: change.originalText,
      modifiedText: change.modifiedText,
      principle,
    })
  }

  return decorations
}

export interface ProofreadProgress {
  progress: number
  decorations: ProofreadDecoration[]
  completed: number
  total: number
}

export const ProofreadMutation = graphql(
  `
  mutation Proofread($input: ProofreadInput!) {
    proofread(input: $input) {
      ... on MutationProofreadSuccess {
        data {
          text
        }
      }
      ...ErrorFragment
    }
  }
`,
  [ErrorFragment],
)

type ProofreadMutationFn = (
  input: VariablesOf<typeof ProofreadMutation>["input"],
) => Promise<string | null>

export async function proofreadForPrinciple({
  postId,
  editor,
  chunks,
  principle,
  locale,
  onProgress,
  executeMutation,
}: {
  postId: string
  editor: Editor
  chunks: TextChunk[]
  principle: Principle
  locale?: Locale
  onProgress?: (progress: ProofreadProgress) => void
  executeMutation: ProofreadMutationFn
}) {
  if (chunks.length === 0) return 0

  const allDecorations: ProofreadDecoration[] = []
  let completedCount = 0

  async function processChunk(textChunk: TextChunk, index: number) {
    const context = chunks
      .slice(0, index)
      .map(c => c.text)
      .join("\n\n")

    try {
      const modifiedText = await executeMutation({
        postId,
        chunk: textChunk.text,
        context,
        principle,
        locale: locale ?? undefined,
      })
      const chunkDecorations = processChunkResult({
        editor,
        chunk: textChunk,
        modifiedText,
        principle,
      })

      allDecorations.push(...chunkDecorations)
      completedCount++

      if (onProgress) {
        const sortedDecorations = [...allDecorations].sort(
          (a, b) => a.from - b.from,
        )
        onProgress({
          progress: (completedCount / chunks.length) * 100,
          decorations: sortedDecorations,
          completed: completedCount,
          total: chunks.length,
        })
      }

      return chunkDecorations
    } catch (error) {
      console.error(`Proofread error for chunk (${principle}):`, error)
      completedCount++

      if (onProgress) {
        const sortedDecorations = [...allDecorations].sort(
          (a, b) => a.from - b.from,
        )
        onProgress({
          progress: (completedCount / chunks.length) * 100,
          decorations: sortedDecorations,
          completed: completedCount,
          total: chunks.length,
        })
      }

      return []
    }
  }

  const indexedChunks = chunks.map((c, i) => ({ chunk: c, index: i }))
  const batches = chunk(indexedChunks, BATCH_SIZE)

  for (const batch of batches) {
    await Promise.all(
      batch.map(({ chunk: c, index }) => processChunk(c, index)),
    )
  }

  return allDecorations.length
}
